import React, { useEffect, useMemo, useState, useCallback } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FiUser,
  FiCalendar,
  FiClipboard,
  FiSettings,
  FiLogOut,
  FiMenu,
  FiImage,
  FiCreditCard,
  FiExternalLink,
  FiGift,
} from "react-icons/fi";
import { useAuthStore } from "../../Store/auth";
import { API_ORIGIN } from "../../config/api";

// --- API
const API = API_ORIGIN;

// Helpers
const normalize = (x) => (Array.isArray(x) ? x : x?.$values ?? []);

const daysBetween = (toISO) => {
  if (!toISO) return null;
  const now = new Date();
  const to = new Date(toISO);
  return Math.ceil((to.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

// Existing early access is shown only for coaches who already have it.
const deriveSubscription = (coach) => {
  if (!coach) {
    return {
      state: "none",
      label: "No plan",
      planLabel: "Free",
      daysLeft: null,
      expiresAt: null,
      isPaidStripe: false,
    };
  }

  // Tier from backend (enum: 0 None, 1 Basic, 2 Pro/Standard)
  const tier = coach.subscriptionTier ?? coach.subscriptionPlan ?? null;

  let planLabel = "Free";
  if (tier === 1 || tier === "Basic" || tier === "basic") {
    planLabel = "Legacy Basic";
  } else if (
    tier === 2 ||
    tier === "Pro" ||
    tier === "Standard" ||
    tier === "pro"
  ) {
    planLabel = "Lead Access";
  }

  // Status from backend enum (0 Inactive, 1 Active, 2 PastDue, 3 Canceled, 4 Expired)
  const rawStatus = coach.subscriptionStatus ?? coach.subscription_status ?? null;
  const s = String(rawStatus ?? "").toLowerCase();

  let state = "none";
  let label = "No plan";

  if (s === "1" || s === "active") {
    state = "active";
    label = "Active";
  } else if (
    s === "2" ||
    s === "pastdue" ||
    s === "past_due" ||
    s === "past-due"
  ) {
    state = "past_due";
    label = "Past due";
  } else if (s === "3" || s === "canceled" || s === "cancelled") {
    state = "canceled";
    label = "Canceled";
  } else if (s === "4" || s === "expired") {
    state = "expired";
    label = "Expired";
  }

  // Expiry date (free access end OR Stripe period end)
  const expiresAt =
    coach.currentPeriodEndUtc ||
    coach.subscriptionExpiresAtUtc ||
    coach.subscriptionExpiryUtc ||
    null;

  const daysLeft = expiresAt ? daysBetween(expiresAt) : null;

  // Stripe paid subscription?
  const stripeSubId =
    coach.stripeSubscriptionId ||
    coach.stripe_subscription_id ||
    coach.subscriptionId ||
    null;
  const isPaidStripe = !!stripeSubId;

  // ✅ Early Access FREE: If no Stripe subscription AND expiresAt is in the future => Early Access
  const now = new Date();
  if (!isPaidStripe && expiresAt) {
    const exp = new Date(expiresAt);
    if (exp > now) {
      state = "free";
      label = `Early Access (Free • ends ${formatDate(expiresAt)})`;
      planLabel = "Early Access";
    } else {
      state = "expired";
      label = "Free listing; lead access unavailable";
      planLabel = "Free";
    }
  }

  return { state, label, planLabel, daysLeft, expiresAt, isPaidStripe };
};

export default function DashboardLayout() {
  const { token, coachId, logout } = useAuthStore();
  const navigate = useNavigate();

  // --- Persisted sidebar state
  const initialOpen = () => {
    const saved = localStorage.getItem("ptn.sidebarOpen");
    if (saved !== null) return saved === "1";
    return window.innerWidth >= 1024;
  };
  const [isSidebarOpen, setIsSidebarOpen] = useState(initialOpen);
  useEffect(() => {
    localStorage.setItem("ptn.sidebarOpen", isSidebarOpen ? "1" : "0");
  }, [isSidebarOpen]);

  const toggleSidebar = () => setIsSidebarOpen((v) => !v);

  // --- Route guard for not logged in
  useEffect(() => {
    if (!token || !coachId) {
      navigate("/login", { replace: true, state: { from: location } });
    }
  }, [token, coachId, navigate, location]);

  // --- API client with auth
  const auth = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // --- Coach & counters
  const [coach, setCoach] = useState(null);
  const [pendingBookings, setPendingBookings] = useState(0);
  const [newGifts, setNewGifts] = useState(0);

  const fetchCoach = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/Coaches/${coachId}`, auth);
      setCoach(res.data);
    } catch (e) {
      if (e?.response?.status === 401) logout?.();
    }
  }, [coachId, auth, logout]);

  const fetchPending = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/Booking/coach/${coachId}`,
        auth
      );
      const list = Array.isArray(data) ? data : normalize(data);
      const count = list.filter((b) => b?.status === 0).length;
      setPendingBookings(count);
    } catch {
      // silent
    }
  }, [coachId, auth]);

  const fetchNewGifts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/Gifts/coach/${coachId}`, auth);
      const list = Array.isArray(data) ? data : normalize(data);
      const count = list.filter(
        (g) => String(g?.status || "").toLowerCase() === "succeeded"
      ).length;
      setNewGifts(count);
    } catch {
      // silent
    }
  }, [coachId, auth]);

  useEffect(() => {
    fetchCoach();
    fetchPending();
    fetchNewGifts();
    const t1 = setInterval(fetchPending, 30000);
    const t2 = setInterval(fetchNewGifts, 30000);
    return () => {
      clearInterval(t1);
      clearInterval(t2);
    };
  }, [fetchCoach, fetchPending, fetchNewGifts]);

  const subscription = deriveSubscription(coach);

  // ✅ Active means: paid active OR free early access
  const isSubActive =
    subscription.state === "active" ||
    subscription.state === "past_due" ||
    subscription.state === "free";

  const initials = (coach?.fullName || "")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleLogout = () => {
    try {
      logout();
    } catch {}
    localStorage.removeItem("token");
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-white">
      {/* Sidebar */}
      <aside
        className={`${
          isSidebarOpen ? "w-64" : "w-18"
        } bg-gray-900 text-white flex flex-col py-4 px-2 lg:px-3 transition-all duration-300 ease-in-out overflow-hidden`}
      >
        {/* Header */}
        <div className="flex items-center gap-2 mb-4 px-2">
          <button
            aria-label="Toggle sidebar"
            onClick={toggleSidebar}
            className="p-2 rounded hover:bg-gray-800"
          >
            <FiMenu size={20} />
          </button>
          <div
            className={`flex items-center gap-3 ${
              isSidebarOpen ? "" : "hidden"
            }`}
          >
            <div className="w-8 h-8 rounded-full bg-white/10 grid place-items-center text-sm font-semibold">
              {coach?.profileImage ? (
                <img
                  src={coach.profileImage}
                  alt="avatar"
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <span>{initials || "PT"}</span>
              )}
            </div>
            <div className="leading-tight">
              <div className="text-sm font-bold truncate max-w-[9rem]">
                {coach?.fullName || "Coach"}
              </div>
              <div className="text-[11px] text-gray-300 truncate max-w-[9rem]">
                {coach?.email || "—"}
              </div>
            </div>
          </div>
        </div>

        {/* Status chips (✅ only subscription now) */}
        <div className={`${isSidebarOpen ? "px-2" : "px-0"}`}>
          <div className="flex flex-col gap-2">
            <StatusChip
              open={isSidebarOpen}
              icon={<FiCreditCard />}
              label={`${subscription.planLabel || "Plan"} • ${
                subscription.label || "—"
              }`}
              tone={
                isSubActive
                  ? "ok"
                  : subscription.state === "past_due"
                  ? "warn"
                  : "muted"
              }
              onClick={() => navigate("/dashboard/subscription")}
              title="Subscription status"
            />
          </div>
        </div>

        {/* Nav */}
        <nav className="flex flex-col gap-1 mt-4">
          {/* Always allowed */}
          <NavItem
            to="profile"
            icon={<FiUser />}
            label="Profile"
            open={isSidebarOpen}
          />

          {/* Gallery — free listing tool */}
          <NavItem
            to="CoachGallery"
            icon={<FiImage />}
            label="Gallery"
            open={isSidebarOpen}
            disabled={false}
          />

          {/* Availabilities — free listing tool */}
          <NavItem
            to="availabilities"
            icon={<FiCalendar />}
            label="Availabilities"
            open={isSidebarOpen}
            disabled={false}
          />

          {/* Bookings — free listing tool */}
          <NavItem
            to="bookings"
            icon={<FiClipboard />}
            label="Bookings"
            open={isSidebarOpen}
            badge={pendingBookings || undefined}
            title={
              pendingBookings ? `${pendingBookings} pending` : undefined
            }
            disabled={false}
          />

          {/* Settings — always allowed */}
          <NavItem
            to="settings"
            icon={<FiSettings />}
            label="Settings"
            open={isSidebarOpen}
          />

          {/* Thanks / Gifts — ALWAYS UNLOCKED */}
          <NavItem
            to="gifts"
            icon={<FiGift />}
            label="Thanks"
            open={isSidebarOpen}
            badge={newGifts || undefined}
            title={newGifts ? `${newGifts} gifts` : undefined}
          />
        </nav>

        {/* Footer */}
        <div className="mt-auto pt-3 border-t border-gray-800">
          <button
            onClick={() => window.open(`/coach/${coachId}`, "_blank")}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded hover:bg-gray-800 text-gray-200 ${
              isSidebarOpen ? "justify-start" : "justify-center"
            }`}
            title="Open public profile"
          >
            <FiExternalLink />
            {isSidebarOpen && <span>Public Profile</span>}
          </button>

          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded hover:bg-gray-800 text-red-300 ${
              isSidebarOpen ? "justify-start" : "justify-center"
            }`}
            title="Logout"
          >
            <FiLogOut />
            {isSidebarOpen && <span>Logout</span>}
          </button>

          {isSidebarOpen && (
            <div className="text-[10px] text-gray-400 px-3 pt-2">
              PTfinderNow · v1.0
            </div>
          )}
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 bg-gray-50 overflow-y-auto">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-10 bg-white border-b px-3 py-2 flex items-center gap-2">
          <button
            aria-label="Toggle sidebar"
            onClick={toggleSidebar}
            className="p-2 rounded hover:bg-gray-100"
          >
            <FiMenu />
          </button>
          <div className="font-semibold">Coach Panel</div>
          <div className="ml-auto text-xs text-gray-500">{coach?.fullName}</div>
        </div>

        <div className="p-4 md:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile overlay when open */}
      {isSidebarOpen && (
        <button
          className="fixed inset-0 bg-black/30 lg:hidden"
          aria-label="Close sidebar"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
}

function NavItem({
  to,
  icon,
  label,
  open,
  badge,
  title,
  disabled = false,
  onBlocked,
}) {
  return (
    <NavLink
      to={to}
      title={title || label}
      onClick={(e) => {
        if (disabled) {
          e.preventDefault();
          e.stopPropagation();
          if (onBlocked) onBlocked();
        }
      }}
      className={({ isActive }) => {
        const active = isActive && !disabled;
        let base =
          "group relative mx-1 flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ";
        base += active
          ? "bg-gray-800 text-white "
          : "text-gray-300 hover:bg-gray-800 hover:text-white ";
        if (disabled) {
          base += "opacity-60 cursor-not-allowed";
        }
        return base;
      }}
    >
      <span className="text-lg shrink-0">{icon}</span>
      <span className={`${open ? "block" : "hidden"}`}>{label}</span>
      {badge ? (
        <span
          className={`ml-auto ${
            open ? "" : "absolute right-2"
          } text-[11px] px-2 py-0.5 rounded-full bg-rose-500 text-white`}
        >
          {badge}
        </span>
      ) : null}
      {disabled && open && !badge && (
        <span className="ml-auto text-[10px] uppercase tracking-wide text-amber-300">
          Upgrade
        </span>
      )}
    </NavLink>
  );
}

function StatusChip({ open, icon, label, tone = "muted", onClick, title }) {
  const toneClasses =
    tone === "ok"
      ? "bg-emerald-600/20 text-emerald-200 border-emerald-500/40"
      : tone === "warn"
      ? "bg-amber-600/20 text-amber-200 border-amber-500/40"
      : tone === "bad"
      ? "bg-rose-600/20 text-rose-200 border-rose-500/40"
      : "bg-gray-700/60 text-gray-300 border-gray-600/60";
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`w-full ${
        open ? "px-3 py-2" : "p-2"
      } rounded-md border ${toneClasses} flex items-center gap-2 hover:brightness-110 transition`}
    >
      <span className="text-sm">{icon}</span>
      {open && <span className="text-xs font-medium truncate">{label}</span>}
    </button>
  );
}
